import type { Aqueduct4ObjectPin, Aqueduct4SourceClosure } from '../../types/aqueduct4';
import type { RefdataFeature, RefdataManifest } from '../../types/refdata';

export const AQUEDUCT4_ARCHIVE_SHA256 = 'bd3ed2bce88d6ff1b89191632ad134a2436e1e1d49599382f23a04d513624fc3';
export const AQUEDUCT4_SOURCE_URL = 'https://www.wri.org/data/aqueduct-40-current-and-future-global-maps-data';
export const AQUEDUCT4_SOURCE_LABEL = 'WRI Aqueduct 4.0 (2023 release; modeled historical water risk)';
export const AQUEDUCT4_GEOMETRY_POLICY = 'unquantized-cell-clipping-valid-native-and-explicit-unknown-component-envelopes';
export const AQUEDUCT4_US_ENVELOPES = [[-125, 24, -66, 50], [-180, 51, -129, 73], [-161, 18, -154, 23],
  [-68, 17, -64, 19], [144, 13, 146, 21], [-172, -15, -168, -10], [172, 51, 180, 54],
  [160, -1, 180, 31], [-180, -16, -150, 31], [-76, 17, -64, 21]] as const;

function validPin(value: unknown): value is Aqueduct4ObjectPin {
  if (!value || typeof value !== 'object') return false;
  const pin = value as Aqueduct4ObjectPin;
  return typeof pin.path === 'string' && /^refdata\/wri-aqueduct\/[a-zA-Z0-9._-]+\/(?:raw|audit|acceptance)\/[a-zA-Z0-9/._-]+$/.test(pin.path)
    && !pin.path.split('/').includes('..') && typeof pin.generation === 'string' && /^[1-9]\d*$/.test(pin.generation)
    && typeof pin.sha256 === 'string' && /^[a-f0-9]{64}$/.test(pin.sha256) && Number.isSafeInteger(pin.bytes) && pin.bytes > 0;
}

export function isAqueduct4SourceClosure(value: unknown, version: string): value is Aqueduct4SourceClosure {
  if (!value || typeof value !== 'object') return false;
  const source = value as Aqueduct4SourceClosure;
  return source.schemaVersion === 1 && source.edition === '4.0' && source.version === version
    && validPin(source.sourceArchive) && source.sourceArchive.sha256 === AQUEDUCT4_ARCHIVE_SHA256
    && source.sourceArchive.bytes === 261527511
    && source.sourceArchive.path === 'refdata/wri-aqueduct/2026-09-08-source-archive/raw/aqueduct-4.0/aqueduct-4-0-water-risk-data.zip'
    && validPin(source.sourceAudit) && validPin(source.compilerReceipt)
    && source.nativeAnnualFeatures === 68506 && source.comparedAnnualAttributes === 231
    && Number.isSafeInteger(source.servingSourceFeatures) && source.servingSourceFeatures > 0 && source.servingSourceFeatures <= source.nativeAnnualFeatures
    && Number.isSafeInteger(source.unlocatedEmptyNativeFeatures) && source.unlocatedEmptyNativeFeatures === 12
    && Number.isSafeInteger(source.unknownNativeFeatures) && source.unknownNativeFeatures >= 1531 && source.unknownNativeFeatures < source.nativeAnnualFeatures
    && source.geometryPolicy === AQUEDUCT4_GEOMETRY_POLICY
    && JSON.stringify(source.servingExtent) === JSON.stringify(AQUEDUCT4_US_ENVELOPES)
    && Array.isArray(source.cellIds) && source.cellIds.length > 0 && source.cellIds.length <= 30000
    && new Set(source.cellIds).size === source.cellIds.length
    && source.cellIds.every(id => typeof id === 'string' && /^[ns]\d{4}[ew]\d{5}_p50$/.test(id))
    && !!source.cellFiles && typeof source.cellFiles === 'object' && !Array.isArray(source.cellFiles)
    && Object.keys(source.cellFiles).length === source.cellIds.length
    && source.cellIds.every(id => {
      const cell = source.cellFiles[id];
      return !!cell && typeof cell.sha256 === 'string' && /^[a-f0-9]{64}$/.test(cell.sha256)
        && Number.isSafeInteger(cell.jsonBytes) && cell.jsonBytes > 0 && cell.jsonBytes <= 32 * 1024 ** 2
        && Number.isSafeInteger(cell.featureCount) && cell.featureCount > 0;
    });
}

export function validAqueduct4Manifest(manifest: Partial<RefdataManifest>): boolean {
  if (manifest.datasetId !== 'wri-aqueduct') return manifest.aqueduct4Sources === undefined;
  return manifest.schemaVersion === 1 && manifest.minimumReaderSchemaVersion === undefined
    && manifest.kind === 'vector' && manifest.geometryStorage === 'cell-clipped' && manifest.cellSizeDeg === 0.5
    && manifest.sourceUrl === AQUEDUCT4_SOURCE_URL && typeof manifest.version === 'string'
    && isAqueduct4SourceClosure(manifest.aqueduct4Sources, manifest.version)
    && manifest.featureCount === manifest.aqueduct4Sources.servingSourceFeatures && manifest.cellShards === undefined;
}

export function isAqueduct4Feature(feature: RefdataFeature): boolean {
  const props = feature?.props;
  if (!props || feature.geom?.type !== 'MultiPolygon' || typeof props.string_id !== 'string'
    || typeof props.wri4SourceGeometrySha256 !== 'string' || !/^[a-f0-9]{64}$/.test(props.wri4SourceGeometrySha256)) return false;
  if (props.wri4GeometryStatus === 'native-valid') return feature.id === props.string_id && props.wri4NativeComponentIndex === null;
  return props.wri4GeometryStatus === 'unknown-component-envelope' && typeof props.wri4NativeComponentIndex === 'number'
    && Number.isSafeInteger(props.wri4NativeComponentIndex) && props.wri4NativeComponentIndex >= 0
    && feature.id === `unknown:${props.string_id}:${props.wri4NativeComponentIndex}`;
}
